import {
    MedicationGroupATCs,
    MedicationGroupNames,
    MedicationGroups,
    MedicationIndicators,
    MedicationSearchBuilder
} from "pokal_api"
import notNull from "../../util/notNull"
import MedicationAnswer from "../abstract/MedicationAnswer"
import BoolQuestionAnswerStrategy from "../abstract/BoolQuestionAnswerStrategy"
import MedicationQuestion from "../abstract/MedicationQuestion"
import QuestionCreator from "../abstract/QuestionCreator"
import MedicationDose from "../abstract/MedicationDose"
import { CombiMedication } from "../abstract/CombiMedication"

export const PatientTakesMedicineQuestionString = (
    search: MedicationSearchBuilder,
    min: number | null,
    max: number | null,
    reason?: string
) =>
    `Nimmt die Patient:in ${min !== null ? `mindestens ${min}${max !== null ? " / " : ""}` : ""}${max !== null ? `maximal ${max}` : ""} der folgenden Medikamente / Medikamentengruppen mit der angegebenen Mindestdosierung${reason !== undefined ? " aus folendem Grund" : ""}:${reason !== undefined ? " " + reason : ""}\n${search.singlesToString()}\n${search.groupsToString()}`

/**
 *
 * @param search the medication to search for
 * @param answer the answer that follows upon success (i.e. found medication in range of min max
 * @param optional answer strategy, if ON_YES answer is triggered on success if ON_NO answer is triggered on failure
 * @param min the min (inclusive) defaults to 1
 * @param max the max (inclusive) defaults to null (i.e. unset)
 * @param logging whether to output debug logs (defaults to false)
 */
const PatientTakesMedicine =
    (
        search: MedicationSearchBuilder,
        answer: MedicationAnswer,
        optional: {
            strategy: BoolQuestionAnswerStrategy
            alternativeAnswer?: MedicationAnswer
        } = {
            strategy: BoolQuestionAnswerStrategy.ON_YES
        },
        min: number | null = 1,
        max: number | null = null,
        logging: boolean = false
    ): QuestionCreator =>
    (input) =>
        new MedicationQuestion(
            PatientTakesMedicineQuestionString(search, min, max),
            input
        ).answerAutomatically((input, problematicATCs) => {
            if (logging) console.log("TAKES MEDICINE CALLED")

            let found: MedicationDose[] = []
            for (const elem of search.singles) {
                const matches = input.patientTakes(elem)
                if (notNull(matches)) {
                    found.push(...matches)
                }
            }
    
            for (const elem of search.groups) {
                if (logging) console.log("Checking group:", MedicationGroupNames[elem.entry])
                const matches = input.patientTakesGroup(elem)
                if (logging) console.log("Matches found for group:", matches)
                if (notNull(matches)) {
                    found.push(...matches)
                }
            }

            console.log(found)


            //TODO: Nils, please confirm
            //In variable found, there are all the atc codes and doses that matched
            //So, we just subtract all the ATCs from question before this one to get the ones that only matched in this instance correct?
            //Problematic ATCs is an optional for backward compatibility reasons. We can make it non-optional in the future.
            //This should not be checked in the context of indicator 22.

            if (
                problematicATCs &&
                !answer
                    .getTriggeredIndicators()
                    .withoutArgs.includes(MedicationIndicators.RISK_OF_SEROTONIN_SYNDROME_2) &&
                !answer
                    .getTriggeredIndicators()
                    .withoutArgs.includes(MedicationIndicators.RISK_OF_RISK_OF_HYPONATRIAEMIE_2) &&
                !answer
                    .getTriggeredIndicators()
                    .withoutArgs.includes(MedicationIndicators.RISK_OF_UNWANTED_KARDIOVASKULAER_EFFECTS_5)
            ) {
                const beforeFilter = [...found];
                found = found.filter((f) => !problematicATCs.includes(f.atc));
                if (logging) console.log("ProblematicATCs:", problematicATCs);
                if (logging) console.log("Removed medications:", beforeFilter.filter(m => !found.includes(m)));
            }

            let count = found.length;
            if (logging) console.log("Base count (before CombiMedication):", count);

            for (const med of found) {
                let match: number | undefined = CombiMedication.get(med.atc)
                if (!match) {
                    for (const [key, value] of CombiMedication.entries()) {
                        if (med.atc.startsWith(key)) {
                            match = value
                            break
                        }
                    }
                }
                if (match) count += match - 1
            }

            if (
                answer
                    .getTriggeredIndicators()
                    .withoutArgs.includes(
                        MedicationIndicators.RISK_OF_UNWANTED_KARDIOVASKULAER_EFFECTS_6
                    )
            ) {
                let yellowListCount = 0
                const yellowList = MedicationGroupATCs[
                    MedicationGroups.YELLOW_LIST
                ].map((elem) => elem.atc)
                for (const elem of [...input.comedication.map((e) => e.atc)]) {
                    for (const yellow of yellowList) {
                        if (elem.startsWith(yellow)) {
                            yellowListCount += 1
                            break
                        }
                    }
                }
                if (yellowListCount >= 2) count = count - yellowListCount // Subtract yellow list count from total count only if more than 1 matches with yellow list
                if (logging) console.log("Yellow list count subtracted:", yellowListCount);
                if (logging) console.log("Final count after yellow list adjustment:", count);
            }

            const minCaseFulfilled: boolean = min === null || count >= min
            const maxCaseFulfilled: boolean = max === null || count <= max
            const success = minCaseFulfilled && maxCaseFulfilled

            if (logging) console.log("Min requirement:", min, "Max requirement:", max);
            if (logging) console.log("Min fulfilled:", minCaseFulfilled, "Max fulfilled:", maxCaseFulfilled);

            if (!success) {
                if (optional.strategy === BoolQuestionAnswerStrategy.ON_NO)
                    return answer
                else
                    return optional.alternativeAnswer
                        ? optional.alternativeAnswer
                        : new MedicationAnswer("Nein")
            }

            // Success case starts here

            if (optional.strategy === BoolQuestionAnswerStrategy.ON_YES)
                return answer.addProblematicACTs(found.map((elem) => elem.atc))
            else
                return optional.alternativeAnswer
                    ? optional.alternativeAnswer.addProblematicACTs(
                          found.map((elem) => elem.atc)
                      )
                    : new MedicationAnswer("Ja").addProblematicACTs(
                          found.map((elem) => elem.atc)
                      )
        })

export default PatientTakesMedicine
